import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useState } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import ProductReview from '@src/quince-site-common/src/domains/ProductReview'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import productDynamicDataStateManager from '@src/quince-site-common/src/redux/globalStateManager/productDynamicDataStateManager/ProductDynamicDataStateManager'
import reviewPopupStateManager from '@src/quince-site-common/src/redux/globalStateManager/ReviewPopupStateManager'

import { If } from '../conditionals'
import ProductReviewHighLight, { ProductReviewHighLightProps } from '../productReviewHighlight'
import ProductReviewGallerySlider from '../reviews/productReviewGallerySlider'

import { showReviewSection } from './reviewHighLightsSection.helpers'
import styles from './reviewHighLightsSection.module.scss'
import stylesV2 from './reviewHighlightsSectionV2.module.scss'
import ReviewHighlightsSummary from './ReviewHighlightsSummary'

export interface ReviewHighLightsSectionProps {
  header?: string
  highLights: ProductReviewHighLightProps[]
  className?: string
  product: Product | Bundle
  newPdpv2?: boolean
}

const ReviewHighLightsSection = ({ highLights = [], ...props }: ReviewHighLightsSectionProps) => {
  const { t } = useTranslation('common')
  const size = highLights.length
  const showAllReviews = (size > 3 ? true : false) && !props.newPdpv2
  const arr = size > 3 ? highLights.slice(0, 3) : highLights
  const finalStyles = props.newPdpv2 ? stylesV2 : styles
  const classes = classnames(finalStyles.container, props.className)

  const getIsVisibleTrackParams = useCallback(() => {
    return {
      action: 'pdp_accordion_reviews',
      value: true,
      context: {
        productId: props.product.spid,
        productName: props.product.title,
      },
    }
  }, [props.product.id])

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const averageRating = props.product.productReviewSummary?.averageRating
  const reviewCount = props.product.productReviewSummary?.reviewCount
  const isHighlightSummaryVisible = props.newPdpv2
  const [photoReviews, setPhotoReviews] = useState<ProductReview[]>([])
  const productReviewImages = productDynamicDataStateManager.getProductReviewImages()

  useEffect(() => {
    setPhotoReviews(productReviewImages)
  }, [productReviewImages])
  const onReviewImageClick = (reviewId: number, imageIndex: number) => {
    reviewPopupStateManager.showReviewPopUp({
      productReviews: photoReviews,
      reviewId: reviewId,
      imageIndex: imageIndex,
    })
  }
  return (
    <div ref={trackIsVisibleRef} className={classes}>
      <div className={finalStyles.container__wrapper}>
        <div
          className={classnames(finalStyles.container__wrapper__header, {
            [`${finalStyles.container__wrapper__header__withoutSummary}`]: !isHighlightSummaryVisible,
            [`${finalStyles.container__wrapper__header__withSummary}`]: isHighlightSummaryVisible,
          })}
        >
          {props.header ?? (
            <span className={finalStyles.container__wrapper__header__title}>{t('review_highlights')}</span>
          )}
          {isHighlightSummaryVisible && (
            <ReviewHighlightsSummary
              averageRating={averageRating}
              reviewCount={reviewCount}
              showReviewSection={showReviewSection}
              newPdpv2={props.newPdpv2}
            />
          )}
        </div>
        {arr.map((highLight, index) => {
          return (
            <ProductReviewHighLight
              key={index}
              {...highLight}
              className={finalStyles.container__wrapper__reviewHighLight}
              newPdpv2={props.newPdpv2}
            />
          )
        })}
        {showAllReviews && (
          <div className={finalStyles.container__wrapper__all} onClick={showReviewSection}>
            {t('read_all_reviews')}
          </div>
        )}

        {props.newPdpv2 && (
          <>
            <If condition={photoReviews.length > 0}>
              <div className={finalStyles.productReviewsImageGallery}>
                <ProductReviewGallerySlider
                  heading={t('gallery')}
                  productReviews={photoReviews}
                  onReviewImageClick={onReviewImageClick}
                  productTitle={props.product.title}
                  newPdpv2={props.newPdpv2}
                  isHighlightSection={true}
                />
              </div>
            </If>
            <div className={finalStyles.container__wrapper__seeAllReviews} onClick={showReviewSection}>
              <span>{t('see_all_reviews')}</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ReviewHighLightsSection
